// src/services/api.js
import logger from './logger';
import { supabase } from '../lib/supabase';
import TokenStorage from './TokenStorage';
import { API_BASE_URL } from '../config';

class ApiService {
  constructor() {
    // PRODUCTION-READY: Uses environment variable in production, localhost in development
    this.baseURL = `${API_BASE_URL}/api`;
  }

  // Get Supabase access token using Supabase client (official SDK method)
  async getSupabaseToken() {
    try {
      if (!supabase) {
        logger.warn('⚠️ Supabase client not initialized');
        return null;
      }

      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        logger.error('❌ Error getting Supabase session:', error);
        return null;
      }

      if (session?.access_token) {
        logger.debug('✅ Supabase token retrieved from session');
        return session.access_token;
      }

      logger.warn('⚠️ No active Supabase session');
      return null;
    } catch (e) {
      logger.error('❌ Error getting Supabase token:', e);
      return null;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Get Supabase auth token (PRIMARY authentication) - now using official SDK
    const supabaseToken = await this.getSupabaseToken();

    // ===== PHASE 3: GET PLATFORM TOKENS FROM SUPABASE =====
    // NEW: Fetch tokens from Supabase instead of localStorage
    let platformToken = null;

    // Skip token fetch for the /platform/tokens endpoint itself (avoid circular call)
    if (!endpoint.includes('/platform/tokens')) {
      try {
        const tokens = await TokenStorage.getPlatformTokens();
        platformToken = tokens.accessToken;
        logger.debug(`✅ Using ${tokens.platform} token from Supabase`);
      } catch (error) {
        // Fallback to localStorage for backward compatibility
        logger.warn('⚠️ Falling back to localStorage for platform token');
        platformToken = localStorage.getItem('spotify_access_token')
                     || localStorage.getItem('youtube_access_token');
      }
    }

    logger.debug('🔍 API Request Debug:', {
      endpoint,
      hasSupabaseToken: !!supabaseToken,
      hasPlatformToken: !!platformToken,
      source: platformToken ? 'Supabase' : 'None'
    });

    const config = {
      ...options,
      credentials: 'include', // Include Flask session cookies
      headers: {
        'Content-Type': 'application/json',
        // PLATFORM TOKEN: From Supabase (not localStorage!)
        ...(platformToken && { 'Authorization': `Bearer ${platformToken}` }),
        // SUPABASE AUTH: User authentication (X-Supabase-Token header)
        ...(supabaseToken && { 'X-Supabase-Token': supabaseToken }),
        ...(options.headers || {}),
      },
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      logger.error('API Request failed:', error);
      throw error;
    }
  }

  // Auth methods - Using Supabase tokens now
  // getCurrentUser validates Supabase token on backend

  async getCurrentUser() {
    return this.request('/user/me');
  }

  // Playlist methods
  async getPlaylists() {
    return this.request('/playlists');
  }

  async createPlaylist(playlistData) {
    return this.request('/playlists', {
      method: 'POST',
      body: playlistData,
    });
  }

  // Debug method
  async testDatabase() {
    return this.request('/debug/database');
  }

  // ====== MODULE SYSTEM ======

  /**
   * Get list of approved DJ modules from backend
   * @returns {Promise<{success: boolean, modules: Array}>}
   */
  async getModules() {
    try {
      return await this.request('/modules');
    } catch (error) {
      logger.error('Failed to fetch modules:', error);
      return { success: false, modules: [] };
    }
  }

  /**
   * Get a random pre-rendered clip from a pool module
   * @param {string} moduleId - e.g. 'welcome', 'hot_mic', 'ad_libs'
   * @returns {Promise<{success: boolean, audio: string, clip: string, module_id: string}>}
   */
  async getPoolClip(moduleId = 'welcome') {
    try {
      return await this.request(`/dj-pool-clip?module_id=${encodeURIComponent(moduleId)}`);
    } catch (error) {
      logger.error(`Failed to fetch pool clip (${moduleId}):`, error);
      return { success: false };
    }
  }

  // ====== AI METHODS ======

  // General text completion for AI assistance
  async generateTextCompletion(prompt, systemMessage = null, maxTokens = 150) {
    try {
      const data = await this.request('/ai/text-completion', {
        method: 'POST',
        body: {
          prompt: prompt,
          system_message: systemMessage,
          max_tokens: maxTokens
        }
      });
      return data;
    } catch (error) {
      logger.error('Text completion failed:', error);
      return {
        success: false,
        error: error.message,
        text: ""
      };
    }
  }

  // Get AI usage statistics
  async getAIUsageStats() {
    try {
      const data = await this.request('/ai/usage-stats');
      return data;
    } catch (error) {
      logger.error('Failed to get usage stats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get cache statistics
  async getCacheStats() {
    try {
      const data = await this.request('/cache/stats');
      return data;
    } catch (error) {
      logger.error('Failed to get cache stats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ====== ENHANCED RECOMMENDATIONS WITH GENRE INTELLIGENCE ======

  // Enhanced recommendations with automatic genre extraction and context
  async getEnhancedRecommendations(currentTrackData, playlistData = [], options = {}) {
    try {
      const {
        targetCount = 6,
        strategy = 'balanced',
        enableGenreIntelligence = true
      } = options;

      // Get session behavior data from localStorage (skips, completes, removes)
      let sessionBehavior = [];
      try {
        const behaviorKey = 'phantom_user_behavior';
        const storedBehavior = localStorage.getItem(behaviorKey);
        if (storedBehavior) {
          const allBehavior = JSON.parse(storedBehavior);
          // Get only recent session behavior (last 30 minutes)
          const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
          sessionBehavior = allBehavior.filter(action =>
            action.timestamp >= thirtyMinutesAgo
          );
          logger.debug(`📊 Session behavior: ${sessionBehavior.length} actions in last 30 min`);
        }
      } catch (behaviorError) {
        logger.warn('Could not load session behavior:', behaviorError);
      }

      // Use the existing enhanced route that now includes genre intelligence
      const requestData = {
        currentTrackData,
        playlistData,
        targetCount,
        strategy,
        sessionBehavior  // NEW: Include behavior signals
      };

      logger.info('🎵 Enhanced recommendations request:', {
        track: currentTrackData?.name,
        strategy,
        playlistSize: playlistData.length,
        genreIntelligence: enableGenreIntelligence,
        behaviorSignals: sessionBehavior.length
      });

      const data = await this.request('/ai/recommendations-managed', {
        method: 'POST',
        body: requestData
      });

      return data;
    } catch (error) {
      logger.error('Enhanced recommendations failed:', error);
      return {
        success: false,
        error: error.message,
        recommendations: []
      };
    }
  }

  // ====== VECTOR RECOMMENDATION ENGINE ======

  async generateRecommendations(data) {
    try {
      return await this.request('/recommendations/generate', {
        method: 'POST',
        body: data,
      });
    } catch (error) {
      logger.error('Generate recommendations failed:', error);
      return { success: false, error: error.message, tracks: [] };
    }
  }

  async syncBehavior(actions) {
    try {
      return await this.request('/recommendations/sync-behavior', {
        method: 'POST',
        body: { actions },
      });
    } catch (error) {
      logger.error('Sync behavior failed:', error);
      return { success: false, error: error.message };
    }
  }

  async getTasteProfile() {
    try {
      return await this.request('/recommendations/profile');
    } catch (error) {
      logger.error('Get taste profile failed:', error);
      return { success: false, error: error.message };
    }
  }

  async updateDiscoveryLevel(level) {
    try {
      return await this.request('/recommendations/profile/discovery', {
        method: 'PUT',
        body: { level },
      });
    } catch (error) {
      logger.error('Update discovery level failed:', error);
      return { success: false, error: error.message };
    }
  }

  async importPlatformProfile(platform = 'spotify') {
    try {
      return await this.request('/recommendations/profile/import', {
        method: 'POST',
        body: { platform },
      });
    } catch (error) {
      logger.error('Import platform profile failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Legacy method - enhanced to use new backend capabilities
  async generateSmartQueries(currentTrack, artist, genre, strategy = 'balanced', playlistContext = null) {
    try {
      // Convert legacy format to new enhanced format
      const currentTrackData = {
        name: currentTrack,
        artists: [{ name: artist }]
      };

      const playlistData = playlistContext ?
        (Array.isArray(playlistContext) ? playlistContext : []) : [];

      // Use enhanced recommendations
      const result = await this.getEnhancedRecommendations(currentTrackData, playlistData, {
        strategy,
        targetCount: 6
      });

      // Convert back to legacy format for backward compatibility
      if (result.success && result.recommendations) {
        return {
          success: true,
          queries: result.recommendations.map(track =>
            `${track.name} ${track.artists?.[0]?.name || ''}`
          ),
          cost_info: result.cost_info,
          cached: result.cached
        };
      }

      return result;
    } catch (error) {
      logger.error('Smart queries generation failed:', error);
      return {
        success: false,
        error: error.message,
        queries: []
      };
    }
  }
}

const apiService = new ApiService();
export default apiService;